import type {
  ObjectPassportLinkedListMeta,
  ObjectPassportLinkedListResult,
  ObjectPassportLinkedTableRow,
} from './linkedTableTypes'

function toNumber(value: unknown): number | null {
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function parseMeta(raw: unknown, page: number, perPage: number): ObjectPassportLinkedListMeta {
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
    const row = raw as Record<string, unknown>
    return {
      page: toNumber(row.page) ?? page,
      perPage: toNumber(row.per_page) ?? perPage,
      total: toNumber(row.total) ?? 0,
    }
  }
  return { page, perPage, total: 0 }
}

/**
 * Парсит одну строку linked-ответа. `name` берётся из поля строки (если бэк отдал)
 * или из карты обогащения по `object_id`.
 */
export function parseLinkedTableRow(
  raw: unknown,
  enrichByObjectId: ReadonlyMap<number, { name: string; coordinates: unknown }>,
): ObjectPassportLinkedTableRow | null {
  if (raw == null || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  const objectId = toNumber(row.object_id ?? row.object_list_id ?? row.id)
  if (objectId == null || objectId <= 0) return null

  const enrich = enrichByObjectId.get(objectId)
  const inlineName = row.name != null ? String(row.name).trim() : ''

  return {
    objectId,
    name: inlineName || enrich?.name || '—',
    coordinates: enrich?.coordinates ?? null,
  }
}

/**
 * Парсит тело GET /objects/:objectListId/fields/:fieldId/linked.
 */
export function parseLinkedTableResponse(
  raw: unknown,
  page: number,
  perPage: number,
  enrichByObjectId: ReadonlyMap<number, { name: string; coordinates: unknown }> = new Map(),
): ObjectPassportLinkedListResult {
  const root = raw != null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const data = Array.isArray(root.data) ? root.data : []
  const payloads = root.payloads ?? root.payload

  const rows = data
    .map((item) => parseLinkedTableRow(item, enrichByObjectId))
    .filter((item): item is ObjectPassportLinkedTableRow => item != null)

  return {
    rows,
    meta: parseMeta(payloads, page, perPage),
  }
}

/**
 * Собирает карту id → { name, coordinates } из bulk GET /inventory/objects/passport?object_ids=...
 */
export function parsePassportBulkNameMap(raw: unknown): Map<number, { name: string; coordinates: unknown }> {
  const map = new Map<number, { name: string; coordinates: unknown }>()
  const root = raw != null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const data = root.data
  const objects = Array.isArray(data) ? data : []
  for (const item of objects) {
    if (item == null || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const id = toNumber(row.id)
    if (id == null || id <= 0) continue
    const name = String(row.name ?? '').trim()
    map.set(id, {
      name: name || '—',
      coordinates: row.coordinates ?? null,
    })
  }
  return map
}
